import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getMockResponsiveWrapper } from '@/components/mock-components/mock-responsive-wrapper-state';
import { throwDevError } from '@/core/utils/logging';
import { ContentDimensions } from '../../core';

/**
 * Utility type for creating responsive variations of props to go with responsive
 * variations of components.
 */
export type ResponsiveComponentProps<ComponentProps extends ContentDimensions> = Omit<
  ComponentProps,
  keyof ContentDimensions
> &
  Partial<ContentDimensions>;

export interface ResponsiveWrapperProps extends Partial<ContentDimensions> {
  /**
   * Children may be one of the following:
   * - Render function that takes an object containing the dimensions as an argument and
   *   returns a `ReactNode`.
   * - React component that accepts `height` and `width` props, which will be set
   *   using `React.cloneElement`
   */
  children: React.ReactElement | ((dimensions: ContentDimensions) => React.ReactNode);

  /**
   * When specified, this value will be used instead of the responsive
   * width. Useful when you only want the height to be responsive.
   */
  width?: number;

  /**
   * When specified, this value will be used instead of the responsive
   * height. Useful when you only want the width to be responsive.
   */
  height?: number;
}

/**
 * Utility component that fills its container, observes the size, and passes
 * the height and width to `children`.
 */
export function ResponsiveWrapper(props: ResponsiveWrapperProps) {
  // If mock is present, use that instead
  const MockResponsiveWrapper = getMockResponsiveWrapper();
  if (MockResponsiveWrapper !== undefined && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line react-hooks/static-components
    return <MockResponsiveWrapper {...props} />;
  }

  const { children, width: overrideWidth, height: overrideHeight } = props;
  const renderChildren = (width: number, height: number) => {
    if (typeof children === 'function') {
      return children({ width, height });
    }
    return React.cloneElement(children, { width, height });
  };

  // The prop types for `AutoSizer` are a little strange in that they're basically a union type where it's impossible
  // to pass, for example, both `disableWidth` and `disableHeight`. The type of `children` you pass to AutoSizer also
  // changes based on the values for `disableWidth` and `disableHeight` (e.g. if you `disableWidth`, the render prop you
  // pass to `AutoSizer` only gets a `height` value). As a result, we have to do a little bit of extra "work" here with
  // `if` statements to map from our prop types to theirs, even though this looks a little repetitive.

  // If both width and height are set, no need to use AutoSizer
  if (overrideWidth !== undefined && overrideHeight !== undefined) {
    throwDevError('No need to use ResponsiveWrapper if both width and height are static values');
    return (
      <div style={{ width: overrideWidth, height: overrideHeight }}>
        {renderChildren(overrideWidth, overrideHeight)}
      </div>
    );
  }

  // Using `AutoSizer` to manage use within complex flex layouts, which we have many of in cloud.
  // A simpler implementation with a 100% width/height element with a resize observer behaves
  // too greedily in those use cases and would require significantly more careful CSS use by
  // consumers than is reasonable. Note that we use scaledWidth/scaledHeight instead of width/height
  // because these values aren't rounded. This helps to ensure that the children will not overflow
  // the parent due to rounding. See https://github.com/bvaughn/react-virtualized-auto-sizer/issues/57
  // for more details.

  // Just manage height with AutoSizer
  if (overrideWidth !== undefined) {
    return (
      <AutoSizer disableWidth>
        {({ scaledHeight: responsiveHeight }) => renderChildren(overrideWidth, responsiveHeight)}
      </AutoSizer>
    );
  }

  // Just manage width with AutoSizer
  if (overrideHeight !== undefined) {
    return (
      <AutoSizer disableHeight>
        {({ scaledWidth: responsiveWidth }) => renderChildren(responsiveWidth, overrideHeight)}
      </AutoSizer>
    );
  }

  // Manage both width and height with AutoSizer
  return (
    <AutoSizer>
      {({ scaledWidth: responsiveWidth, scaledHeight: responsiveHeight }) =>
        renderChildren(responsiveWidth, responsiveHeight)
      }
    </AutoSizer>
  );
}

export type ResponsiveWrapperComponent = typeof ResponsiveWrapper;
